import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { NotebookPen, Save } from 'lucide-react';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Dialog, DialogContent } from 'components/Dialog';
import { Label } from 'components/Label';
import { Textarea } from 'components/Textarea';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { submitNote } from 'lib/api/submitNote';
import { Note } from 'types/Load';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { TRANSPORT_PRO_PRIORITY_NOTE_TYPE } from '../constants';

interface TransportProNoteFormData {
  note: string;
  isPriority: boolean;
}

interface TransportProNoteCreatorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  loadId: number;
  onNoteCreated: (notes: Note[]) => void;
}

export default function TransportProNoteCreatorDialog({
  isOpen,
  onClose,
  loadId,
  onNoteCreated,
}: TransportProNoteCreatorDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const formMethods = useForm<TransportProNoteFormData>({
    defaultValues: {
      note: '',
      isPriority: false,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods;

  const handleClose = () => {
    reset({ note: '', isPriority: false });
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      reset({ note: '', isPriority: false });
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: TransportProNoteFormData) => {
    setIsLoading(true);

    try {
      const note = data.note.trim();
      const res = await submitNote(
        loadId,
        note,
        undefined,
        undefined,
        undefined,
        {
          noteType: data.isPriority
            ? TRANSPORT_PRO_PRIORITY_NOTE_TYPE
            : undefined,
          appendAuthorSuffix: false,
        }
      );

      if (res.isOk()) {
        toast({
          description: 'Note added successfully.',
          variant: 'success',
        });
        onNoteCreated(res.value);
        handleClose();
        return;
      }

      toast({
        description: res.error.message,
        variant: 'destructive',
      });
    } catch {
      toast({
        description: 'Unable to submit note.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='max-w-md p-0 max-h-[90vh] flex flex-col border border-neutral-300'>
        <FormProvider {...formMethods}>
          <Flex
            justify='between'
            align='center'
            className='bg-brand-main px-4 py-3 text-neutral-50 border-b rounded-t-lg [&>div>svg]:stroke-white!'
          >
            <Flex align='center' gap='sm'>
              <NotebookPen className='h-5 w-5' />
              <Typography variant='h3' className='text-neutral-50'>
                Add Note
              </Typography>
            </Flex>
          </Flex>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
            className='flex flex-1 flex-col overflow-hidden'
          >
            <div className='flex-1 space-y-4 overflow-y-auto px-4 py-4'>
              <div className='w-full'>
                <Label
                  name='note'
                  showAIHint={false}
                  className='text-neutral-700 mb-1'
                >
                  <Typography variant='body' weight='semibold'>
                    Note
                  </Typography>
                </Label>
                <Controller
                  name='note'
                  control={control}
                  rules={{
                    validate: (value) =>
                      value.trim().length > 0 || 'Note is required',
                  }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      placeholder='Enter note text'
                      rows={4}
                      className='mt-2'
                    />
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name='note'
                  render={({ message }) => (
                    <Typography
                      variant='body-sm'
                      className='text-error-500 mt-1'
                    >
                      {message}
                    </Typography>
                  )}
                />
              </div>

              <Controller
                name='isPriority'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id='transportpro-note-dialog-priority'
                    label='Priority note'
                    labelClassName='text-neutral-700 text-sm'
                    checked={field.value}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                    className={
                      field.value
                        ? 'data-[state=checked]:bg-warning data-[state=checked]:border-warning'
                        : undefined
                    }
                  />
                )}
              />
            </div>

            <Flex
              gap='sm'
              className='border-t border-neutral-300 bg-neutral-100 px-4 py-3 rounded-b-lg'
            >
              <Button
                buttonNamePosthog={null}
                type='button'
                variant='outline'
                onClick={handleClose}
                className='flex-1 text-sm'
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                buttonNamePosthog={ButtonNamePosthog.SubmitNote}
                type='submit'
                className='flex-1 gap-2 [&>svg]:stroke-white! text-sm'
                disabled={isLoading}
                logProperties={{ loadID: loadId }}
              >
                {isLoading ? (
                  <ButtonLoader />
                ) : (
                  <>
                    <Save className='h-4 w-4' />
                    {ButtonText.SubmitNote}
                  </>
                )}
              </Button>
            </Flex>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
